import { MobileOfferCatalogEntry } from '@numeo/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Users } from 'lucide-react'
import { mobileOfferError, mobileOffersApi } from './api'
import { StoreOfferPanel } from './StoreOfferPanel'
import { OfferButton, OfferNotice } from './ui'
import { modeLabel, offerDescription, offerLinkClass, planLabels } from './offerLabels'

export function OfferDetailsPage() {
    const { offerId } = useParams<{ offerId: string }>()
    const [offer, setOffer] = useState<MobileOfferCatalogEntry | null>(null)
    const [error, setError] = useState('')
    const sequence = useRef(0)
    const load = useCallback(async () => {
        if (!offerId) return
        const request = ++sequence.current
        setError('')
        try {
            const result = await mobileOffersApi.find(offerId)
            if (request === sequence.current) setOffer(result)
        } catch (failure) {
            if (request === sequence.current) setError(mobileOfferError(failure))
        }
    }, [offerId])
    useEffect(() => {
        setOffer(null)
        void load()
        return () => {
            sequence.current += 1
        }
    }, [load])
    return (
        <div className="space-y-5">
            <Link className={offerLinkClass} to="/billing/mobile-offers">
                <ArrowLeft size={16} />
                Back to offers
            </Link>
            {error && (
                <div className="space-y-3">
                    <OfferNotice error>{error}</OfferNotice>
                    <OfferButton variant="secondary" onClick={() => void load()}>
                        Retry
                    </OfferButton>
                </div>
            )}
            {!offer && !error && <OfferNotice>Loading offer…</OfferNotice>}
            {offer && offer.id === offerId && (
                <>
                    <header className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                {planLabels[offer.terms.plan]} · Monthly subscription
                            </p>
                            <h2 className="mt-1 text-xl font-semibold">{offerDescription(offer.terms)}</h2>
                            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                                {modeLabel(offer.terms)} · {offer.terms.customerEligibilities?.join(', ') || 'All subscriber categories'}
                            </p>
                        </div>
                        <Link to="/billing/mobile-offers/customers" className={offerLinkClass}>
                            <Users size={16} />
                            Find a customer to assign
                        </Link>
                    </header>
                    <details className="rounded-lg border border-slate-200 bg-white p-3 text-sm dark:border-slate-700 dark:bg-slate-900">
                        <summary className="cursor-pointer font-medium focus-visible:ring-2 focus-visible:ring-blue-600">
                            {offer.terms.countries.length} selected countries & regions
                        </summary>
                        <p className="mt-3 leading-relaxed text-slate-500 dark:text-slate-400">{offer.terms.countries.join(', ')}</p>
                    </details>
                    <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
                        <StoreOfferPanel key={`${offer.id}:google`} offer={offer} provider="google" onChanged={load} />
                        <StoreOfferPanel key={`${offer.id}:apple`} offer={offer} provider="apple" onChanged={load} />
                    </div>
                    <OfferNotice>
                        Store configuration does not guarantee availability for every customer. Their storefront and the app’s eligibility checks still apply.
                        An assignment does not start a paid subscription.
                    </OfferNotice>
                </>
            )}
        </div>
    )
}
